import {
  AreaType,
  CreateArea,
  CreateSubArea,
  UpdateArea,
  UpdateSubArea,
} from '@/types/areaZone'
import { SearchParams } from '@/types/common'
import { queryStringify } from '@/ultils'
import { parentAreas } from '@/utils/areaHierarchy'
import axiosClient from '../axiosClient'

const areaApi = {
  getAll(searchParams: SearchParams = {}) {
    const url = '/api/area'
    return axiosClient.get(url + queryStringify(searchParams))
  },
  getByParentId(searchParams: SearchParams = {}, parentId: number) {
    const url = `/api/area/parentid/${parentId}`
    return axiosClient.get(url + queryStringify(searchParams))
  },
  getById(interfaceId: Number, id: Number): Promise<{ data: AreaType }> {
    const url = `/api/area/${interfaceId}/${id}`
    return axiosClient.get(url)
  },
  /**
   * The top-level areas of an interface.
   *
   * SCRUM-1010 QC, 27 Sep: this no longer asks `/api/area/parent/{id}`. That
   * endpoint returns only areas whose parent is the number 0, and on Demo
   * Controller D1 every top-level area has a **null** parent, so it answered
   * `[]` — the Area dropdown in the station drawer read "No options" and the
   * one in the grid offered nothing but the value the row already had.
   *
   * The flat list carries the same areas and is honest about both spellings,
   * so the parents are picked out of it here. The answer keeps the endpoint's
   * `{ id, name }` shape, and arrives as `{ data }` like any other response,
   * so nothing that calls this has to change.
   */
  async getAreaParent(interfaceId: Number) {
    const { data } = await areaApi.getAll({
      interfaceId: Number(interfaceId),
      PageSize: 0,
    })
    return { data: parentAreas(data?.data) }
  },
  getSubAreaParent(interfaceId: Number, parentId: number) {
    const url = `/api/area/sub/${interfaceId}/${parentId}`
    return axiosClient.get(url)
  },

  create(payload: CreateArea) {
    const url = '/api/area/parent'
    return axiosClient.post(url, payload)
  },
  createSubArea(payload: CreateSubArea) {
    const url = '/api/area'
    return axiosClient.post(url, payload)
  },

  updateArea(payload: UpdateArea) {
    const url = 'api/area/parent'
    return axiosClient.put(url, payload)
  },

  updateSubArea(payload: UpdateSubArea) {
    const url = '/api/area'
    return axiosClient.put(url, payload)
  },

  delete(interfaceId: string, id: string) {
    const url = `/api/area/?interfaceId=${interfaceId}&id=${id}`
    return axiosClient.delete(url)
  },
}

export default areaApi
